import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatMoney } from "@/lib/shopify";
import { useT } from "@/lib/i18n";
import { trackCartView, trackCheckoutStarted, trackRemoveFromCart } from "@/lib/analytics";
import { CartAssurance } from "./CartAssurance";
import { CartFeaturedProducts } from "./CartFeaturedProducts";
import { CartPaymentMethods } from "./CartPaymentMethods";

export function CartDrawer() {
  const {
    items,
    isLoading,
    isSyncing,
    isOpen,
    setOpen,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
    syncCart,
  } = useCartStore();
  const { t, tn } = useT();

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce(
    (sum, i) => sum + parseFloat(i.price.amount) * i.quantity,
    0,
  );
  const currency = items[0]?.price.currencyCode ?? "DKK";

  useEffect(() => {
    if (isOpen) {
      syncCart();
      trackCartView({
        itemCount: totalItems,
        totalValue: { amount: totalPrice.toFixed(2), currencyCode: currency },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    // Block checkout if any line is over its known stock — Shopify would
    // otherwise drop the customer on a generic "not available" error page.
    const overstock = items.find(
      (i) => typeof i.quantityAvailable === "number" && i.quantity > i.quantityAvailable
    );
    if (overstock) {
      const max = overstock.quantityAvailable ?? 0;
      const msg =
        max <= 0
          ? `"${overstock.product.node.title}" er desværre udsolgt. Fjern den fra kurven for at gå til kassen.`
          : `Vi har kun ${max} stk. tilbage af "${overstock.product.node.title}". Justér antallet i kurven for at gå til kassen.`;
      window.alert(msg);
      return;
    }
    const url = getCheckoutUrl();
    if (url) {
      trackCheckoutStarted({
        totalValue: { amount: totalPrice.toFixed(2), currencyCode: currency },
        itemCount: totalItems,
      });
      window.open(url, "_blank");
      setOpen(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent className="flex h-full w-full flex-col overflow-y-auto sm:max-w-md lg:max-w-[720px] lg:overflow-hidden lg:p-0">
        <div className="stripe-rainbow -mx-6 -mt-6 mb-2 h-1 lg:mx-0 lg:mt-0 lg:mb-0" aria-hidden="true" />

        <div className="flex flex-col lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[240px_1fr] lg:gap-0">
          {/* Left column (desktop only) — curated discovery */}
          <aside className="hidden min-h-0 lg:flex lg:flex-col lg:border-r lg:border-border lg:bg-muted/20 lg:px-6 lg:py-6">
            {items.length > 0 && (
              <CartFeaturedProducts layout="vertical" onNavigate={() => setOpen(false)} />
            )}
          </aside>

          {/* Right column — the cart itself */}
          <div className="flex flex-col lg:min-h-0 lg:flex-1 lg:px-6 lg:py-6">
            <SheetHeader className="flex-shrink-0">
              <SheetTitle className="font-display text-2xl">{t("cart.title")}</SheetTitle>
              <SheetDescription>
                {totalItems === 0 ? t("cart.empty") : tn("cart.items", totalItems)}
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-col pt-6 lg:min-h-0 lg:flex-1">
              {items.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <ShoppingBag className="mb-4 h-10 w-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t("cart.emptyHelp")}</p>
                  <Link
                    to="/collections"
                    onClick={() => setOpen(false)}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-background hover:bg-terracotta"
                  >
                    {t("cart.browse")}
                  </Link>
                </div>
              ) : (
                <>
                  <div className="pr-1 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                    <ul className="divide-y divide-border">


                      {items.map((item) => {
                        const img = item.product.node.images?.edges?.[0]?.node;
                        const stock = item.quantityAvailable;
                        const knownStock = typeof stock === "number";
                        const atMax = knownStock && item.quantity >= (stock as number);
                        const overStock = knownStock && item.quantity > (stock as number);
                        return (
                          <li key={item.variantId} className="flex gap-4 py-5">
                            <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm bg-muted">
                              {img && (
                                <img
                                  src={img.url}
                                  alt={img.altText ?? item.product.node.title}
                                  loading="lazy"
                                  decoding="async"
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col">
                              <h4 className="font-display text-base leading-tight text-foreground line-clamp-2">
                                {item.product.node.title}
                              </h4>
                              {item.selectedOptions.length > 0 &&
                                item.selectedOptions[0].value !== "Default Title" && (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {item.selectedOptions
                                      .map((o) => `${o.name}: ${o.value}`)
                                      .join(" · ")}
                                  </p>
                                )}
                              <p className="mt-1 text-sm text-foreground">{formatMoney(item.price)}</p>
                              {overStock && (
                                <p className="mt-1.5 text-[11px] leading-snug text-destructive">
                                  Kun {Math.max(stock as number, 0)} på lager — justér antal for at gå til kassen.
                                </p>
                              )}
                              {!overStock && atMax && (
                                <p className="mt-1.5 text-[11px] text-muted-foreground">
                                  Sidste {stock} på lager
                                </p>
                              )}
                              <div className="mt-3 flex items-center justify-between">
                                <div className="inline-flex items-center rounded-full border border-border">
                                  <button
                                    onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                    className="px-2.5 py-1.5 text-foreground/70 hover:text-foreground"
                                    aria-label={t("cart.decrease")}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="min-w-[2ch] text-center text-sm">{item.quantity}</span>
                                  <button
                                    onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                    disabled={atMax}
                                    className="px-2.5 py-1.5 text-foreground/70 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                                    aria-label={t("cart.increase")}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                                <button
                                  onClick={() => {
                                    trackRemoveFromCart({
                                      product: {
                                        id: item.product.node.id,
                                        handle: item.product.node.handle,
                                        title: item.product.node.title,
                                        price: item.price,
                                        variantId: item.variantId,
                                      },
                                      quantity: item.quantity,
                                    });
                                    removeItem(item.variantId);
                                  }}
                                  className="text-muted-foreground hover:text-destructive"
                                  aria-label={t("cart.remove")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <CartAssurance />

                  {/* Mobile-only featured carousel */}
                  <div className="lg:hidden">
                    <CartFeaturedProducts layout="horizontal" onNavigate={() => setOpen(false)} />
                  </div>

                  <div className="flex-shrink-0 space-y-4 bg-background pt-5">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                        {t("cart.subtotal")}
                      </span>
                      <span className="font-display text-2xl text-foreground">
                        {formatMoney({ amount: totalPrice.toString(), currencyCode: currency })}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t("cart.subtotalNote")}</p>
                    <Button
                      onClick={handleCheckout}
                      disabled={isLoading || isSyncing || items.length === 0}
                      className="w-full rounded-full bg-foreground py-6 text-xs font-medium uppercase tracking-[0.22em] text-background hover:bg-terracotta"
                    >
                      {isLoading || isSyncing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          {t("cart.checkout")} <ExternalLink className="ml-2 h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                    <CartPaymentMethods />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
